<template>
    <Head title="Nadia Tutorial" />
    <BaseLayout gap="lg" content-class="relative">
        <div
            v-for="(depth, index) in scrollDepthMarkers"
            :key="depth"
            ref="markerRefs"
            :data-scroll-depth="depth"
            :class="['pointer-events-none absolute inset-x-0 h-0', markerClasses[index]]"
            aria-hidden="true" />

        <header class="flex flex-col gap-2">
            <p class="body-small-semibold uppercase tracking-wider text-accent">Nadia Tutorial</p>
            <h1 class="display-serif text-ink">What is Nadia?</h1>
            <p class="body max-w-2xl text-ink-muted">
                Nadia is your AI work coach. Talk to her about a tough conversation, a decision you're weighing, or a goal you want to reach, and she'll help you think it through,
                prepare, and follow up over time. Everything you share is private to you.
            </p>
        </header>

        <section class="flex flex-col gap-5">
            <h2 class="title-section">What Nadia can help with</h2>
            <div class="grid gap-3 md:grid-cols-2">
                <BaseCard v-for="card in helpCards" :key="card.title" radius="lgr" class="flex items-start gap-3">
                    <i :class="['bi', card.icon, 'text-xl text-accent']" aria-hidden="true"></i>
                    <div>
                        <p class="body-semibold text-ink">{{ card.title }}</p>
                        <p class="body-small text-ink-muted">{{ card.description }}</p>
                    </div>
                </BaseCard>
            </div>
        </section>

        <section class="flex flex-col gap-5">
            <h2 class="title-section">How to get started</h2>
            <ol class="grid list-none gap-3 p-0 md:grid-cols-3">
                <li v-for="(step, index) in steps" :key="step.title">
                    <BaseCard theme="accent" radius="lgr" class="flex h-full flex-col gap-2">
                        <span class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent-soft body-small-bold text-ink">{{ index + 1 }}</span>
                        <p class="body-semibold text-ink">{{ step.title }}</p>
                        <p class="body-small text-ink-muted">{{ step.description }}</p>
                    </BaseCard>
                </li>
            </ol>
        </section>

        <section v-if="videos.length" class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <h2 class="title-section">Feature walkthroughs</h2>
                <p class="body-small text-ink-muted">Short videos for the tools your organization has turned on.</p>
            </div>
            <div class="flex flex-col">
                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <WalkthroughVideoCard v-for="(video, index) in initialVideos" :key="video.id" :video="video" @select="openVideo(video, index + 1)" />
                </div>
                <BaseCollapsible v-if="overflowVideos.length > 0" v-model:open="showAllVideos">
                    <template #content>
                        <div class="grid gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
                            <WalkthroughVideoCard
                                v-for="(video, index) in overflowVideos"
                                :key="video.id"
                                :video="video"
                                @select="openVideo(video, initialVideos.length + index + 1)" />
                        </div>
                    </template>
                </BaseCollapsible>
            </div>
            <BaseButton v-if="overflowVideos.length > 0 && !showAllVideos" theme="tertiary" class="self-start" @click="showAllVideos = true">
                Show all {{ videos.length }} walkthroughs
            </BaseButton>
        </section>

        <section class="flex flex-col gap-5">
            <h2 class="title-section">Try one of these</h2>
            <div class="flex flex-col gap-3">
                <BaseCard
                    v-for="(prompt, index) in prompts"
                    :key="prompt.id"
                    as="button"
                    type="button"
                    class="flex w-full items-center justify-between gap-4 text-left body text-ink disabled:opacity-60"
                    :disabled="isFetching"
                    @click="startPrompt(prompt, index + 1)">
                    <span>"{{ prompt.text }}"</span>
                    <i class="bi bi-arrow-right text-accent" aria-hidden="true"></i>
                </BaseCard>
            </div>
            <p v-if="chatError" role="alert" class="body text-status-error">{{ chatError }}</p>
            <BaseLink :href="`${start_chat_url}?chat_source=nadia_tutorial_page`" class="base-btn-primary inline-flex items-center gap-2 self-start">
                <i class="bi bi-plus-lg" aria-hidden="true"></i>
                Start a new chat
            </BaseLink>
        </section>

        <footer class="flex flex-wrap gap-6 border-t border-border pt-5 body-small text-ink-muted">
            <span class="min-w-0">
                Questions about your data?
                <BaseButton type="button" theme="tertiary" size="sm" class="max-w-full whitespace-normal wrap-anywhere text-left" @click="privacyDialogOpen = true">
                    {{ organizationDetails?.privacy_link_label || "Read the privacy notice" }}
                </BaseButton>
            </span>
            <span>
                Something not working?
                <a :href="isInIFrame() ? report_issue_url : globalProperties?.$feedbackMailto" class="text-accent hover:text-accent-hover">Report an issue</a>
            </span>
        </footer>
    </BaseLayout>
    <PrivacyNoticeDialog v-model:open="privacyDialogOpen" />
    <WalkthroughVideoDialog v-model:open="videoDialogOpen" :video="activeVideo" :position="activeVideoPosition" />
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import { useNadiaTutorialAnalytics, useNadiaTutorialPageClosed, useNadiaTutorialScrollDepth } from "~vue/composables/useNadiaTutorialAnalytics";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import WalkthroughVideoCard from "~vue/NadiaTutorial/WalkthroughVideoCard.vue";
import WalkthroughVideoDialog from "~vue/NadiaTutorial/WalkthroughVideoDialog.vue";
import { type WalkthroughVideo, walkthroughVideos } from "~vue/NadiaTutorial/walkthroughVideos";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants";
import { analyticsSourceFromQuery } from "~vue/utils/logUtils";
import type { ComputedRef } from "vue";
import { inject, onMounted, ref } from "vue";

interface Prompt {
    id: string;
    text: string;
}

interface StartChatResponse {
    redirect_url?: string;
}

interface StartChatPayload {
    chat_source: string;
    initial_message: string;
}

const props = withDefaults(
    defineProps<{
        start_chat_url: string;
        report_issue_url: string;
        walkthrough_video_ids: string[];
        max_visible_videos?: number;
    }>(),
    { max_visible_videos: 6 },
);

defineOptions({ layout: LeftSidebarLayout });

const globalProperties = inject<GlobalProperties>("globalProperties");
const organizationDetails = inject<ComputedRef<OrganizationDetails>>("organization_details");
const { logTutorialEvent } = useNadiaTutorialAnalytics();
const { markerRefs } = useNadiaTutorialScrollDepth(logTutorialEvent);
useNadiaTutorialPageClosed(logTutorialEvent);
const privacyDialogOpen = ref(false);
const videoDialogOpen = ref(false);
const activeVideo = ref<WalkthroughVideo | null>(null);
const activeVideoPosition = ref(0);
const showAllVideos = ref(false);
const chatError = ref<string | null>(null);
const chatPayload = ref<StartChatPayload | null>(null);
const prompts: Prompt[] = [
    { id: "prepare_conversation", text: "Help me prepare for a difficult conversation with a teammate." },
    { id: "feedback_plan", text: "I got some tough feedback. Help me turn it into a plan." },
    { id: "stuck_decision", text: "I'm stuck on a decision. Help me think it through." },
];

const {
    data: chatResponse,
    error: chatRequestError,
    execute: executeChat,
    isFetching,
} = useFetch(props.start_chat_url, { immediate: false }).post(chatPayload).json<StartChatResponse>();

const helpCards = [
    {
        icon: "bi-chat-dots",
        title: "Prepare for conversations",
        description: "Rehearse feedback, 1:1s, and difficult discussions before they happen.",
    },
    {
        icon: "bi-bullseye",
        title: "Set and track goals",
        description: "Turn a vague ambition into concrete next steps and check in on progress.",
    },
    {
        icon: "bi-people",
        title: "Work better with your team",
        description: "Understand working styles and get advice tailored to the people you work with.",
    },
    {
        icon: "bi-clipboard-check",
        title: "Performance and reviews",
        description: "Draft self-assessments and prepare for review conversations.",
    },
];
const steps = [
    {
        title: "Start a chat",
        description: "Click New Chat and describe what's on your mind in your own words.",
    },
    {
        title: "Give context",
        description: "Share who's involved and what outcome you want. The more Nadia knows, the sharper the advice.",
    },
    {
        title: "Come back",
        description: "Nadia remembers past conversations, so follow up after the meeting or the week.",
    },
];
const videos = walkthroughVideos.filter((video) => props.walkthrough_video_ids.includes(video.id));
const initialVideos = videos.slice(0, props.max_visible_videos);
const overflowVideos = videos.slice(props.max_visible_videos);
const scrollDepthMarkers = [25, 50, 75, 100] as const;
const markerClasses = ["top-[25%]", "top-[50%]", "top-[75%]", "bottom-0"] as const;

function openVideo(video: WalkthroughVideo, position: number) {
    activeVideo.value = video;
    activeVideoPosition.value = position;
    videoDialogOpen.value = true;
}

async function startPrompt(prompt: Prompt, position: number) {
    if (isFetching.value) {
        return;
    }

    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_CTA_CLICKED, { prompt_id: prompt.id, position });
    chatError.value = null;
    chatPayload.value = {
        chat_source: CHATSOURCE.NADIA_TUTORIAL_PAGE,
        initial_message: prompt.text,
    };

    await executeChat();
    if (chatRequestError.value || !chatResponse.value?.redirect_url) {
        chatError.value = "We couldn't start this conversation. Please try again.";
        return;
    }

    if (isInIFrame()) {
        globalThis.location.href = chatResponse.value.redirect_url;
    } else {
        router.visit(chatResponse.value.redirect_url);
    }
}

onMounted(() => {
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_PAGE_VIEWED, {
        source: analyticsSourceFromQuery("direct"),
    });
});
</script>
